import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDataTable } from "@/hooks/use-data-table";
import type { Invitation } from "@/types/invitation";
import { Head, router, useForm, usePage } from "@inertiajs/react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  LinkIcon,
  PencilIcon,
  RefreshCwIcon,
  XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { type FormEvent, useMemo, useState } from "react";

const roleLabels: Record<string, string> = {
  admin: "Administrador",
  member: "Membro",
  superadmin: "Super Admin",
};

const statusLabels: Record<string, string> = {
  pending: "Pendente",
  accepted: "Aceito",
  expired: "Expirado",
};

const statusVariants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "secondary",
  accepted: "default",
  expired: "outline",
};

function EditRoleDialog({ invitation }: { invitation: Invitation }) {
  const [open, setOpen] = useState(false);
  const { data, setData, patch, processing } = useForm({
    invitation: { role: invitation.role as string },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    patch(`/app/invitations/${invitation.id}`, {
      onSuccess: () => setOpen(false),
    });
  }

  if (invitation.status !== "pending" || invitation.role === "superadmin") return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Editar papel">
          <PencilIcon className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Editar Papel</DialogTitle>
            <DialogDescription>
              Altere o papel do convite para {invitation.email}.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel>Papel</FieldLabel>
              <Select
                value={data.invitation.role}
                onValueChange={(value) =>
                  setData("invitation", { ...data.invitation, role: value })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="member">Membro</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={processing}>
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Index({
  invitations,
}: {
  invitations: Invitation[];
}) {
  const { user: currentUser } = usePage().props;
  const isSuperadmin = currentUser?.role === "superadmin";

  const columns = useMemo<ColumnDef<Invitation>[]>(
    () => [
      {
        id: "email",
        accessorKey: "email",
        enableColumnFilter: true,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="E-mail" />
        ),
        meta: {
          label: "E-mail",
          variant: "text" as const,
          placeholder: "Filtrar por e-mail...",
        },
      },
      {
        id: "role",
        accessorKey: "role",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Papel" />
        ),
        meta: {
          label: "Papel",
        },
        cell: ({ row }) => (
          <Badge variant="outline">
            {roleLabels[row.original.role] ?? row.original.role}
          </Badge>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        enableColumnFilter: true,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Status" />
        ),
        cell: ({ row }) => (
          <Badge variant={statusVariants[row.original.status] ?? "secondary"}>
            {statusLabels[row.original.status] ?? row.original.status}
          </Badge>
        ),
        meta: {
          label: "Status",
          variant: "select" as const,
          options: [
            { label: "Pendente", value: "pending" },
            { label: "Aceito", value: "accepted" },
            { label: "Expirado", value: "expired" },
          ],
        },
      },
      {
        id: "inviterName",
        accessorKey: "inviterName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Convidou" />
        ),
        meta: {
          label: "Convidou",
        },
      },
      ...(isSuperadmin
        ? [
            {
              id: "organizationName",
              accessorKey: "organizationName",
              header: ({ column }: { column: any }) => (
                <DataTableColumnHeader column={column} label="Organização" />
              ),
              cell: ({ row }: { row: any }) =>
                row.original.organizationName ?? "—",
              meta: {
                label: "Organização",
              },
            } satisfies ColumnDef<Invitation>,
          ]
        : []),
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Criado em" />
        ),
        cell: ({ row }) =>
          new Date(row.original.createdAt).toLocaleDateString("pt-BR"),
        meta: {
          label: "Criado em",
        },
      },
      {
        id: "expiresAt",
        accessorKey: "expiresAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Expira em" />
        ),
        cell: ({ row }) =>
          new Date(row.original.expiresAt).toLocaleString("pt-BR", {
            dateStyle: "short",
            timeStyle: "short",
          }),
        meta: {
          label: "Expira em",
        },
      },
      {
        id: "actions",
        cell: ({ row }) => {
          const inv = row.original;
          return (
            <div className="flex items-center gap-1">
              <EditRoleDialog invitation={inv} />
              {inv.status === "pending" && inv.acceptanceUrl && (
                <Button
                  variant="ghost"
                  size="icon"
                  title="Copiar link"
                  onClick={() => {
                    navigator.clipboard.writeText(inv.acceptanceUrl!);
                    toast.success("Link copiado!");
                  }}
                >
                  <LinkIcon className="size-4" />
                </Button>
              )}
              {inv.status === "pending" && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Reenviar"
                    onClick={() =>
                      router.post(`/app/invitations/${inv.id}/resend`)
                    }
                  >
                    <RefreshCwIcon className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Cancelar"
                    onClick={() => {
                      if (confirm("Cancelar este convite?")) {
                        router.delete(`/app/invitations/${inv.id}`);
                      }
                    }}
                  >
                    <XIcon className="size-4" />
                  </Button>
                </>
              )}
              {inv.status === "expired" && (
                <Button
                  variant="ghost"
                  size="icon"
                  title="Reenviar"
                  onClick={() =>
                    router.post(`/app/invitations/${inv.id}/resend`)
                  }
                >
                  <RefreshCwIcon className="size-4" />
                </Button>
              )}
            </div>
          );
        },
        size: 120,
      },
    ],
    [isSuperadmin],
  );

  const { table } = useDataTable({
    data: invitations,
    columns,
    pageCount: Math.ceil(invitations.length / 10),
    enableRowSelection: false,
  });

  return (
    <>
      <Head title="Convites" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Convites</h1>
        <p className="text-muted-foreground text-sm">
          Gerencie os convites da organização.
        </p>
      </div>
      <DataTable table={table}>
        <DataTableToolbar table={table} />
      </DataTable>
      </div>
    </>
  );
}
